import React, { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    Card,
    Checkbox,
    Descriptions,
    Grid,
    Space,
    Spin,
    Tag,
    Typography,
} from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import { useAuth } from '../../auth/AuthContext';
import { signOutApp } from '../../auth/signOutApp';
import {
    readDisableHardDuplicateModal,
    readDisableSoftPreSaveModal,
    writeDisableHardDuplicateModal,
    writeDisableSoftPreSaveModal,
} from '../DataBaseV3/QuestionBankMigration/migrationSaveModalPreferences';
import {
    DIFFICULTIES,
    DIFFICULTY_OPTIONS,
    TOPIC_OPTIONS,
    TOPICS,
} from '../DataBaseV3/Questions/QuestionsConstant';
import './AccountPage.css';

const { Title, Paragraph, Text } = Typography;

function formatFirebaseTime(iso: string | undefined): string {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
        });
    } catch {
        return iso;
    }
}

function AccountPage() {
    const screens = Grid.useBreakpoint();
    const isNarrow = !screens.md;
    const { user, role, roleLoading, roleDenied } = useAuth();
    const [signingOut, setSigningOut] = useState(false);
    const [disableSoftPreSaveModal, setDisableSoftPreSaveModal] = useState(() => readDisableSoftPreSaveModal());
    const [disableHardDuplicateModal, setDisableHardDuplicateModal] = useState(() => readDisableHardDuplicateModal());

    useEffect(() => {
        writeDisableSoftPreSaveModal(disableSoftPreSaveModal);
    }, [disableSoftPreSaveModal]);

    useEffect(() => {
        writeDisableHardDuplicateModal(disableHardDuplicateModal);
    }, [disableHardDuplicateModal]);

    const handleSignOut = async () => {
        setSigningOut(true);
        try {
            await signOutApp();
        } finally {
            setSigningOut(false);
        }
    };

    const email = user?.email ?? null;
    const displayName = user?.displayName?.trim() || null;

    const profileItems = [
        {
            key: 'email',
            label: 'Email',
            children: email ? <Text copyable={{ text: email }}>{email}</Text> : <Text type="secondary">—</Text>,
        },
        {
            key: 'uid',
            label: 'User ID',
            children: user?.uid ? (
                <Text code copyable={{ text: user.uid }}>
                    {user.uid}
                </Text>
            ) : (
                '—'
            ),
        },
        ...(displayName
            ? [
                  {
                      key: 'name',
                      label: 'Display name',
                      children: <Text>{displayName}</Text>,
                  },
              ]
            : []),
        {
            key: 'verified',
            label: 'Email verified',
            children: user?.emailVerified ? (
                <Tag color="success">Yes</Tag>
            ) : (
                <Tag color="warning">No</Tag>
            ),
        },
        {
            key: 'role',
            label: 'Admin role',
            children: roleLoading ? (
                <Spin size="small" />
            ) : role ? (
                <Tag color="blue">{role}</Tag>
            ) : (
                <Text type="secondary">—</Text>
            ),
        },
        {
            key: 'created',
            label: 'Account created',
            children: <Text type="secondary">{formatFirebaseTime(user?.metadata?.creationTime)}</Text>,
        },
        {
            key: 'last',
            label: 'Last sign-in',
            children: <Text type="secondary">{formatFirebaseTime(user?.metadata?.lastSignInTime)}</Text>,
        },
    ];

    return (
        <div className="account-page vw-section-stack">
            <header className="account-page__header">
                <Title level={3} className="account-page__title">
                    Account
                </Title>
                <Paragraph type="secondary" className="account-page__lead">
                    Signed-in profile and session. Use Sign out here or from the top bar.
                </Paragraph>
            </header>

            {roleDenied ? (
                <Alert
                    type="warning"
                    showIcon
                    message="No admin role"
                    description="You are signed in, but no VehiWheel admin role was found. Some areas may be unavailable."
                    className="account-page__alert"
                />
            ) : null}

            <Card title="Profile" className="account-page__card">
                <Descriptions
                    column={isNarrow ? 1 : 2}
                    size="small"
                    layout="horizontal"
                    items={profileItems}
                />
            </Card>

            <Card title="Session" className="account-page__card">
                <Space direction="vertical" size="middle" className="account-page__session">
                    <Paragraph type="secondary" className="account-page__session-hint">
                        Signing out clears your session in this browser.
                    </Paragraph>
                    <Button
                        type="primary"
                        danger
                        icon={<LogoutOutlined />}
                        loading={signingOut}
                        onClick={handleSignOut}
                    >
                        Sign out
                    </Button>
                </Space>
            </Card>

            <Card title="Supported question topics" className="account-page__card">
                <Paragraph type="secondary" className="account-page__prefs-lead">
                    Closed list for question <Text code>topic</Text> metadata and Test Catalog BY_TOPICS groups (
                    {TOPICS.length} slugs). Test display names may use indexing (e.g. “Road signs 1”) — that is separate
                    from the slug below. See{' '}
                    <Text code>docs/docsForUI/CATALOG_TOPIC_TAXONOMY_V1.md</Text> for AI scope definitions.
                </Paragraph>
                <Space size={[8, 8]} wrap className="account-page__topic-tags">
                    {TOPIC_OPTIONS.map((o) => (
                        <Tag key={o.value} title={o.label}>
                            <Text code>{o.value}</Text>
                        </Tag>
                    ))}
                </Space>
                <Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 8 }}>
                    Difficulties ({DIFFICULTIES.length})
                </Paragraph>
                <Space size={[8, 8]} wrap>
                    {DIFFICULTY_OPTIONS.map((o) => (
                        <Tag key={o.value} color="blue">
                            <Text code>{o.value}</Text>
                        </Tag>
                    ))}
                </Space>
            </Card>

            <Card title="Preferences" className="account-page__card">
                <Paragraph type="secondary" className="account-page__prefs-lead">
                    Question bank migration tools can show confirmation dialogs before each save. Defaults keep all
                    dialogs on. Use the options below to turn them off in this browser (stored in local storage).{' '}
                    <Text strong>Either</Text> “Disable soft pre-save” <Text strong>or</Text> “Disable hard duplicate”
                    skips the read-only “Review IDs before saving to Firebase” step on batch (and the single-tab
                    pre-save summary).
                </Paragraph>
                <Space direction="vertical" size="middle" className="account-page__prefs-migration">
                    <Checkbox
                        checked={disableSoftPreSaveModal}
                        onChange={(e) => setDisableSoftPreSaveModal(e.target.checked)}
                    >
                        <Text strong>Disable soft pre-save modal</Text>
                        <Paragraph type="secondary" className="account-page__prefs-hint">
                            Skips the read-only summary (what will be saved: ids, topic, preview) on Question Bank
                            Migration and the batch “Review IDs…” step before Firebase writes. If you leave this
                            unchecked but turn on “Disable hard duplicate…” below, the batch review is skipped anyway
                            (same streamlined path).
                        </Paragraph>
                    </Checkbox>
                    <Checkbox
                        checked={disableHardDuplicateModal}
                        onChange={(e) => setDisableHardDuplicateModal(e.target.checked)}
                    >
                        <Text strong>Disable hard duplicate / similar-id guard</Text>
                        <Paragraph type="secondary" className="account-page__prefs-hint">
                            Skips warnings when another question shares the same stripped id base (e.g. hash siblings).
                            Batch runs will not pause for that check before each write. Also skips the same read-only
                            Firebase ID review as above so runs go straight to writes (with auto bump on common id
                            collisions after state save). Use only if you accept the overlap risk.
                        </Paragraph>
                    </Checkbox>
                </Space>
            </Card>
        </div>
    );
}

export default AccountPage;
